import { IDatasetConfig, DatasetActionOpenType, ListViewItemTypes, FieldViews, IPaginationResponse } from 'app/interfaces';
import { IDataContainerService } from 'app/interfaces/data-container.interface';
import { fieldsMap, TYPE_PAYMENT_METHOD, TYPE_VAT_CODE } from './fields';
import paymentForm from './forms/payment.form';
import vatForm from './forms/vat.form';

/**
 * Due sezioni distinte sullo stesso dataset server_rt_mappings: il tipo è imposto dalla voce di
 * menu (getCallParams sulla lista, campo type nascosto nel form), così ogni schermata mostra
 * solo le colonne e i campi che le competono.
 */

/**
 * Avviso quando il profilo Kiara Cloud non è leggibile: senza di esso i select restano vuoti
 */
function getServerUnreachableReview(paginationResponse: IPaginationResponse<any>, metaReview: any): string{
	const meta = paginationResponse.meta || {};
	if(!meta[metaReview]) return '';
	return '<span class="red-fg">Server RT non raggiungibile: i codici disponibili non sono'
		+ ' selezionabili e le mappature non possono essere verificate.</span>';
}

function targetCodeColumn(title: string): any{
	return {
		title,
		key: 'target_code',
		fieldView: FieldViews.HTML,
		getDisplayHTML(record: any): string{
			// <em> e non <i>: in Fuse il tag i e' quello delle icone (font-size 24px in _icons.scss)
			let html = record.target_code || '<em class="secondary-text">Nessuna (IVA ordinaria)</em>';
			if(record.target_description && record.target_code) html += ' - ' + record.target_description;
			if(record.target_missing){
				html += ' <span class="material-icons-outlined red-fg" style="vertical-align:middle;font-size:18px;"'
					+ ' title="Codice non presente nel profilo Server RT">warning</span>';
			}
			return html;
		}
	};
}

const sourceCodeColumn = {
	title: 'Codice Tangerine',
	key: 'source_code',
	fieldView: FieldViews.HTML,
	getDisplayHTML(record: any): string{
		const description = record.source_description || record.source_code || '';
		return '<b>' + description + '</b><br><small>' + (record.source_code || '') + '</small>';
	}
};

function buildConfig(options: {
	name: string,
	title: string,
	singleTitle: string,
	type: string,
	form: any,
	columns: any[],
	docLink: string
}): IDatasetConfig{
	return {
		name: options.name,
		title: options.title,
		singleTitle: options.singleTitle,
		ajaxDatasetCode: 'server_rt_mappings',
		docLink: options.docLink,
		fieldsMap: fieldsMap,
		enabledActions: {
			detail: true,
			create: true,
			edit: true,
			delete: true,
			list: true
		},
		formConfigsMap: {
			generalForm: options.form
		},
		defaultActions: {
			detail: {
				openType: DatasetActionOpenType.DIALOG,
				beforeCalls: [
					{
						name: 'record',
						path: '/dataset/server_rt_mappings/detail/:recordId'
					}
				],
				viewConfig: {
					formConfig: options.form,
					actions: {
						edit: true,
						delete: true
					}
				}
			},
			create: {
				openType: DatasetActionOpenType.DIALOG,
				getCallParams(dataContainerService: IDataContainerService, parentData: any, params: any): any{
					params.type = options.type;
					return params;
				},
				viewConfig: {
					formConfig: options.form
				}
			},
			edit: {
				openType: DatasetActionOpenType.DIALOG,
				viewConfig: {
					formConfig: options.form
				}
			},
			list: {
				openType: DatasetActionOpenType.PAGE,
				// filtro sul tipo imposto dalla sezione
				getCallParams(dataContainerService: IDataContainerService, parentData: any, params: any): any{
					params.type = options.type;
					return params;
				},
				viewConfig: {
					itemViewType: ListViewItemTypes.TABLE,
					showSearchBar: true,
					defaultSortBy: 'created_at',
					defaultSortDirection: 'desc',
					columns: options.columns,
					actions: ['create', 'detail', 'edit', 'delete'],
					metaReviews: ['rt_server_unreachable'],
					getMetaReview: getServerUnreachableReview
				}
			}
		}
	};
}

export const ServerRtPaymentMappingsConfig: IDatasetConfig = buildConfig({
	name: 'server_rt_payment_mappings',
	title: 'Mappatura pagamenti',
	singleTitle: 'Mappatura metodo di pagamento',
	type: TYPE_PAYMENT_METHOD,
	form: paymentForm,
	docLink: '/docs/touroperators/cantability/rt-payment-mappings.php',
	columns: [
		sourceCodeColumn,
		targetCodeColumn('Codice pagamento Server RT')
	]
});

export const ServerRtVatMappingsConfig: IDatasetConfig = buildConfig({
	name: 'server_rt_vat_mappings',
	title: 'Mappatura IVA',
	singleTitle: 'Mappatura fiscalità',
	type: TYPE_VAT_CODE,
	form: vatForm,
	docLink: '/docs/touroperators/cantability/rt-vat-mappings.php',
	columns: [
		sourceCodeColumn,
		{
			title: 'Aliquota Server RT',
			key: 'vat_rate',
			fieldView: FieldViews.HTML,
			getDisplayHTML(record: any): string{
				if(record.vat_rate === null || record.vat_rate === undefined) return 'Da fiscalità';
				return (record.vat_rate * 100) + '%';
			}
		},
		targetCodeColumn('Natura IVA Server RT'),
		{
			title: 'Dicitura',
			key: 'notes'
		}
	]
});
